import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { initializeApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  Messaging,
  MessagePayload
} from 'firebase/messaging';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageService } from '../core/service/localStorage/local-storage.service';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class FirebaseMessagingService {
  private messaging!: Messaging;
  private messageSubject = new BehaviorSubject<MessagePayload | null>(null);
  private tokenSubject = new BehaviorSubject<string | null>(null);

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private local: LocalStorageService, private http: HttpClient) {
    // Only initialize if we're in a browser AND service workers are supported
    if (isPlatformBrowser(this.platformId) && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      try {
        const app = initializeApp(environment.firebase);
        this.messaging = getMessaging(app);
        this.initializeMessaging();
      } catch (error) {
        console.error('Failed to initialize Firebase Messaging:', error);
      }
    }
  }

  /**
   * Initialize Firebase messaging and set up message listener
   */
  private initializeMessaging(): void {
    if (!this.messaging) return;

    // Listen for foreground messages
    onMessage(this.messaging, (payload) => {
      console.log('Foreground message received:', payload);
      this.messageSubject.next(payload);
      this.showNotification(payload);
    });
  }

  /**
   * Request notification permission and get FCM token
   */
  async requestPermissionAndGetToken(): Promise<string | null> {
    if (!isPlatformBrowser(this.platformId) || !this.messaging) {
      return null;
    }

    console.log('Requesting notification permission...');

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        console.log('Notification permission granted.');

        // Get or register the service worker
        let serviceWorkerRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');

        if (!serviceWorkerRegistration) {
          console.log('Registering new service worker...');
          serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
            scope: '/'
          });
        }

        // Wait for service worker to be ready
        if (serviceWorkerRegistration.installing) {
          console.log('Service worker installing, waiting for activation...');
          await new Promise<void>((resolve) => {
            serviceWorkerRegistration!.installing!.addEventListener('statechange', function () {
              if (this.state === 'activated') {
                console.log('Service worker activated');
                resolve();
              }
            });
          });
        } else if (serviceWorkerRegistration.waiting) {
          console.log('Service worker waiting, activating...');
          serviceWorkerRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
          await new Promise<void>((resolve) => {
            navigator.serviceWorker.addEventListener('controllerchange', () => {
              console.log('Service worker controller changed');
              resolve();
            }, { once: true });
          });
        }

        // Ensure service worker is active and ready
        await navigator.serviceWorker.ready;
        console.log('Service worker is ready');

        // Get FCM token
        const token = await getToken(this.messaging, {
          vapidKey: environment.firebase.vapidKey,
          serviceWorkerRegistration: serviceWorkerRegistration
        });

        if (token) {
          console.log('FCM Token obtained successfully');
          this.tokenSubject.next(token);

          // Store token in localStorage for later use
          localStorage.setItem('fcm_token', token);

          return token;
        } else {
          console.log('No registration token available.');
          return null;
        }
      } else {
        console.log('Unable to get permission to notify.');
        return null;
      }
    } catch (error) {
      console.error('An error occurred while retrieving token:', error);
      return null;
    }
  }

  /**
   * Get stored FCM token from localStorage
   */
  getStoredToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('fcm_token');
    }
    return null;
  }

  /**
   * Observable for receiving messages
   */
  getMessage(): Observable<MessagePayload | null> {
    return this.messageSubject.asObservable();
  }

  /**
   * Observable for FCM token changes
   */
  getToken(): Observable<string | null> {
    return this.tokenSubject.asObservable();
  }

  /**
   * Show browser notification for foreground messages
   */
  private showNotification(payload: any): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!('Notification' in window)) {
      console.log('This browser does not support notifications.');
      return;
    }

    if (Notification.permission === 'granted') {
      const notificationTitle = payload.notification?.title || 'New Message';
      const notificationOptions = {
        body: payload.notification?.body || '',
        icon: payload.notification?.image || '/assets/icons/icon-192x192.png',
        badge: '/assets/icons/badge-72x72.png',
        tag: 'firebase-notification',
        requireInteraction: true,
        actions: [
          {
            action: 'view',
            title: 'View',
            icon: '/assets/icons/view-icon.png'
          },
          {
            action: 'dismiss',
            title: 'Dismiss',
            icon: '/assets/icons/dismiss-icon.png'
          }
        ],
        data: payload.data
      };

      const notification = new Notification(notificationTitle, notificationOptions);

      // Handle notification click
      notification.onclick = () => {
        window.focus();
        notification.close();

        // Handle custom actions based on data payload
        if (payload.data?.url) {
          window.open(payload.data.url, '_blank');
        }
      };

      // Auto close notification after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);
    }
  }

  /**
   * Send token to your backend server
   */
  // async sendTokenToServer(token: string): Promise<boolean> {
  //   try {
  //     // Replace with your actual backend endpoint
  //     const response = await fetch('/api/fcm-tokens', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         // Add authentication headers if needed
  //       },
  //       body: JSON.stringify({
  //         token: token,
  //         userId: this.getCurrentUserId(), // Implement this method
  //         device: this.getDeviceInfo()
  //       })
  //     });

  //     if (response.ok) {
  //       console.log('Token sent to server successfully');
  //       return true;
  //     } else {
  //       console.error('Failed to send token to server');
  //       return false;
  //     }
  //   } catch (error) {
  //     console.error('Error sending token to server:', error);
  //     return false;
  //   }
  // }

  async sendTokenToServer(token: string | null) {
    console.log('💾 Token to save:', token);
    
    // Check if token is already cached and sent to server
    const cachedToken = localStorage.getItem('fcm_token');
    if (cachedToken === token) {
      console.log('Token already sent to server, skipping API calls');
      return;
    }

    let user = await this.loadCurrentUser();
    let payload = {
      "agentId": user?.id,
      "fcmToken": token,
      "agentRole": user?.roles?.[0]?.name,
    };
    
    // Send to your backend
    this.http.post('https://waba.crizac.com/api/v1/agents/register-fcm-token', payload).subscribe({
      next: () => {
        console.log('Token registered successfully');
        // Cache the token after successful registration
        if (token) {
          localStorage.setItem('fcm_token', token);
        }
      },
      error: (error) => {
        console.error('Error registering token:', error);
      }
    });

    let pushNotUrl = `${environment.apiURL}v1/api/auth/subscribeOnsignal?device_type=${6}&userId=${user?.id}&playerId=${token}`

    this.http.post(pushNotUrl, {}).subscribe({
      next: () => {
        console.log('OneSignal subscription successful');
      },
      error: (error) => {
        console.error('Error subscribing to OneSignal:', error);
      }
    });
  }

  async loadCurrentUser() {
    let swtichUser = JSON.parse(
      this.local.getKey('isSwitchUser') || 'false'
    );
    if (!swtichUser) {
      let user = await this.local.getKeyIndexedDbKey(
        'currentUser'
      );
      return user;
    } else {
      let user = await this.local.getKeyIndexedDbKey(
        'switchUser'
      );
      return user;
    }

  }


  /**
   * Get current user ID (implement based on your auth system)
   */
  private getCurrentUserId(): string | null {
    // Implement this based on your authentication system
    // For example, from localStorage, sessionStorage, or auth service
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('user_id');
    }
    return null;
  }

  /**
   * Get device information for token management
   */
  private getDeviceInfo(): any {
    if (!isPlatformBrowser(this.platformId)) {
      return {};
    }
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Delete FCM token (for logout or when user wants to stop notifications)
   */
  async deleteToken(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    try {
      // Note: deleteToken is not available in v9 modular SDK
      // You should handle token deletion on your backend

      // Remove from localStorage
      localStorage.removeItem('fcm_token');
      this.tokenSubject.next(null);

      // Optionally notify your backend to delete the token
      const token = this.getStoredToken();
      if (token) {
        await this.removeTokenFromServer(token);
      }

      return true;
    } catch (error) {
      console.error('Error deleting token:', error);
      return false;
    }
  }

  /**
   * Remove token from backend server
   */
  private async removeTokenFromServer(token: string): Promise<void> {
    try {
      await fetch('/api/fcm-tokens', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token })
      });
    } catch (error) {
      console.error('Error removing token from server:', error);
    }
  }

  /**
   * Check if notifications are supported and permitted
   */
  isNotificationSupported(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return 'Notification' in window && 'serviceWorker' in navigator;
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus(): NotificationPermission {
    if (!isPlatformBrowser(this.platformId)) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Subscribe to topic (for topic-based messaging)
   */
  async subscribeToTopic(topic: string): Promise<boolean> {
    const token = this.getStoredToken();
    if (!token) {
      console.error('No FCM token available for topic subscription');
      return false;
    }

    try {
      // This should be done on your backend server
      // as client-side topic subscription is not directly supported
      const response = await fetch('/api/subscribe-topic', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          topic: topic
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Error subscribing to topic:', error);
      return false;
    }
  }

  /**
   * Unsubscribe from topic
   */
  async unsubscribeFromTopic(topic: string): Promise<boolean> {
    const token = this.getStoredToken();
    if (!token) {
      console.error('No FCM token available for topic unsubscription');
      return false;
    }

    try {
      const response = await fetch('/api/unsubscribe-topic', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          topic: topic
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Error unsubscribing from topic:', error);
      return false;
    }
  }
}